import { ChangeEvent, DragEvent, FC, useId, useRef, useState } from "react";
import { infoIcon, uploadBlackIcon } from "utils/constant";

interface FileUploadProps {
  label?: string;
  fileTypes?: string;
  required?: boolean;
  multiple?: boolean;
  info?: string;
  onChange: (file: File | null) => void;
}

const FileUpload: FC<FileUploadProps> = ({
  label = "Attachment",
  fileTypes = "image/*",
  required = false,
  multiple = false,
  info = "",
  onChange,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  // Unique per instance — multiple uploaders on one page must not share an id,
  // otherwise every label click triggers the first uploader's input.
  const fieldId = useId();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0] ?? null;
    setFileToState(file);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setFileToState(file);
  };

  const setFileToState = (file: File | null) => {
    // if (file) {
    //   const allowedTypes = fileTypes.split(",").map((type) => type.trim());
    //   const isValidType =
    //     fileTypes === "*" ||
    //     allowedTypes.some(
    //       (type) => file.type.includes(type) || file.name.endsWith(type)
    //     );
    //   if (!isValidType) {
    //     setError(`Invalid file type. Allowed types: ${fileTypes}`);
    //     setSelectedFile(null);
    //     onChange(null);
    //     return;
    //   }
    // }

    setError(null); // Clear any previous error
    setSelectedFile(file);
    onChange(file);
  };

  const clearFile = () => {
    setSelectedFile(null);
    setError(null); // Clear error when clearing the file
    onChange(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="flex flex-col w-full">
      {/* Label */}
      <label
        htmlFor={fieldId}
        className="flex align-top text-gray-700 font-medium mb-2 gap-2"
      >
        {infoIcon}
        {label}
        {required && (
          <span className="text-red-500" title="This field is required">
            *
          </span>
        )}
      </label>

      {/* If a file is selected, show the chip */}
      {selectedFile ? (
        <div className="flex items-center justify-between rounded-md border border-gray-300 bg-gray-50 px-4 py-2">
          <span className="truncate">{selectedFile.name}</span>
          <button
            type="button"
            onClick={clearFile}
            className="ml-4 text-gray-400 hover:text-gray-600"
            aria-label="Remove selected file"
          >
            ×
          </button>
        </div>
      ) : (
        /* Dropzone */
        <div
          className="
            group relative flex cursor-pointer flex-col items-center justify-center
            rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 py-12
            hover:bg-gray-100 transition
          "
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          {uploadBlackIcon}
          <p className="text-gray-500 group-hover:text-gray-600">
            Upload a file here
          </p>
        </div>
      )}

      {/* Hidden file input */}
      <input
        id={fieldId}
        type="file"
        accept={fileTypes} // Ensure this matches the validation logic
        ref={inputRef}
        multiple={multiple}
        className="hidden"
        onChange={handleChange}
      />

      {/* Hint */}
      {info && <p className="mt-1 text-xs text-gray-500">{info}</p>}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
};

export default FileUpload;
